import * as React from "react";
import { ComponentsHelper } from "../../../../core/services/ComponentHelper";
import { IRepertoireField } from "../../../types/IRepertoireField";
import { isTableAction } from "../../../types/ITableAction";
import { IUsageDetailsRowData } from "../../../types/usageTypes/IUsageDetailsRowData";
import { IUsagePoolState } from "../../../../redux/types/IUsagePoolState";
import { ILookupDictionary } from "../../../../lookup/types/ILookupDictionary";
import { UsageRowView } from "../../../usage/components/usageRowView/UsageRowView";
import DataGridTableRow from "../../usageComponents/dataGridTableRow/DataGridTableRow";
import IRepertoireComponentDataItem from "../../../../redux/types/IRepertoireComponentDataItem";
import {
    CONTRIBUTORS_KEY,
    DISTRIBUTION_POOLS_KEY,
    EDITABLE_FIELDS_GRID,
    IP_LICENSING_REPRESENTATION_KEY,
    MAGAZINE_LEVY_COPY_CRITERIA,
    MAGAZINE_METADATA_PRICE,
    MAGAZINE_METADATA_PUBLISHED,
    NUMBERS_KEY,
    POOL_WEIGHT_SETTINGS_KEY,
    TITLES_KEY,
    USAGE_DETAILS_ROWS_SECTION_KEY,
    USAGE_LICENSE_MATCHING,
    USAGE_MATCHING_PRODUCTS_SECTION_KEY,
    USAGE_MATCHING_WORKS_SECTION_KEY,
    USAGE_PRODUCT_DETAILS_ROWS_SECTION_KEY,
    USAGE_ROWS_SECTION_KEY,
} from "../../../Consts";
import { DRAFT_AGREEMENTS_COMMENTS_STATE_KEY } from "../../../../membersportal/Consts";
import { MEMBERS_PORTAL } from "../../../../accounts/Consts";

interface ITableSectionsResult {
    sections: JSX.Element[];
    dependentSections: JSX.Element[];
}

interface IRenderTableContentsParams {
    tableContents: any;
    paginatedTableContents?: any;
    addUsageDetailsRowData?: any;
    onUpdateUsageRowDetails?: (value: IUsageDetailsRowData[], fieldName: string, index: number) => void;
    isExpandedUsageMatchingSection?: boolean;
    componentInstance: string;
    customFields?: number[];
    pool?: IUsagePoolState;
    dataGridTableData: IRepertoireComponentDataItem;
    isExpandable?: boolean;
    stateKey: any;
    changeData?: any;
    expandedUsageDetailsRow?: number[];
    addUsageDetailsShareIPRow?: any;
    lookupValues?: ILookupDictionary;
    showModal?: any;
    portalType?: string;
    resetMessageBanner?: () => void;
    openWorkFlow?: boolean;
    focusedRow: number;
    currentPage: number;
    resultsPerPage: number;
    currentPageFromProps?: number;
    resultsPerPageFromProps?: number;
    enableCustomField?: (index: number) => void;
    clearAllRowsButThis?: (value: any) => void;
    getTableSections: (
        tableContentItem: any,
        index: any,
        props: any,
        renderRowAction: (tableContentItem: any, field: any) => JSX.Element,
        renderFieldOptions: (field: IRepertoireField, tableContentItem: any, index: number) => JSX.Element,
        changeTableData: (
            value: any,
            stateKey: any,
            objectKey: string,
            rowIndex: number,
            componentInstance?: string,
            newOrderValue?: string
        ) => void,
        showSearchModal: (modalProps: any, searchViewKey: string, index?: number) => void,
        onClickOpen: (field: IRepertoireField, tableContentItem: any) => void
    ) => ITableSectionsResult;
    renderRowAction: (tableContentItem: any, field: any) => JSX.Element;
    renderFieldOptions: (field: IRepertoireField, tableContentItem: any, index: number) => JSX.Element;
    changeTableData: (
        value: any,
        stateKey: any,
        objectKey: string,
        rowIndex: number,
        componentInstance?: string,
        newOrderValue?: string
    ) => void;
    showSearchModal: (modalProps: any, searchViewKey: string, index?: number) => void;
    onClickOpen: (field: IRepertoireField, tableContentItem: any) => void;
    onRowClick: (rowNumber: number) => void;
    dependentRowAction: (tableContentItem: any, event?: React.MouseEvent) => void;
    updateIpRepresentationsIdsToRemove: (tableContentItem: any) => number[];
    handleDrop: (newOrder: any, e: any, droppedOnItem: any, tableContents: any) => void;
    handleDragLeave: (index: any, e: any, tableContentItem: any, tableContents: any) => void;
    handleDragEnd: (index: any, e: any, tableContentItem: any, tableContents: any) => void;
    handleDragEnter: (index: any, e: any, tableContentItem: any, tableContents: any) => void;
    handleDragOver: (index: any, e: any, tableContentItem: any, tableContents: any) => void;
    handleDragStart: (index: any, e: any, draggedItem: any, tableContents: any) => void;
    propsForTableSections: any;
}

const roundDecimals = (numToRound: string | number, digits = 2) => {
    const convertedNumber = Number(numToRound);

    if (!Number.isFinite(convertedNumber)) {
        return numToRound;
    }

    return convertedNumber.toFixed(digits);
};

const PAGINATED_COMPONENTS = [NUMBERS_KEY, TITLES_KEY, POOL_WEIGHT_SETTINGS_KEY];

const PAGINATED_MAGAZINE_COMPONENTS = [
    MAGAZINE_LEVY_COPY_CRITERIA,
    MAGAZINE_METADATA_PRICE,
    MAGAZINE_METADATA_PUBLISHED,
];

const COMPONENTS_WITH_ORIGINAL_INDEX = [
    NUMBERS_KEY,
    DISTRIBUTION_POOLS_KEY,
    MAGAZINE_LEVY_COPY_CRITERIA,
    MAGAZINE_METADATA_PRICE,
    MAGAZINE_METADATA_PUBLISHED,
    TITLES_KEY,
    POOL_WEIGHT_SETTINGS_KEY,
];

const ORDERED_COMPONENTS = [
    CONTRIBUTORS_KEY,
    EDITABLE_FIELDS_GRID,
    DRAFT_AGREEMENTS_COMMENTS_STATE_KEY,
    MAGAZINE_METADATA_PRICE,
    MAGAZINE_METADATA_PUBLISHED,
];

const getPaginatedFieldsWithOriginalIndex = (
    tableContents: any[],
    currentPage: number,
    resultsPerPage: number
) => {
    const startIndex = (currentPage - 1) * resultsPerPage;
    const endIndex = startIndex + resultsPerPage;

    return tableContents.slice(startIndex, endIndex).map((tableContentItem, index) => ({
        ...tableContentItem,
        originalIndex: startIndex + index,
    }));
};

const getDistributionPoolFieldsWithOriginalIndex = (
    tableContents: any[],
    currentPageFromProps: number,
    resultsPerPageFromProps: number
) => {
    const startIndex = (currentPageFromProps - 1) * resultsPerPageFromProps;
    const endIndex = startIndex + resultsPerPageFromProps;

    return tableContents.slice(startIndex, endIndex).map((tableContentItem, index) => ({
        ...tableContentItem,
        originalIndex: startIndex + index,
    }));
};

const orderFieldsToRenderIfNeeded = (
    fieldsToRender: any,
    componentInstance: string
): any => {
    if (!ORDERED_COMPONENTS.includes(componentInstance)) {
        return fieldsToRender;
    }

    let fields: any[] = [];

    if (fieldsToRender.length === undefined) {
        Object.keys(fieldsToRender).forEach(function (key) {
            fields.push(fieldsToRender[key]);
        });

        return ComponentsHelper.orderFieldsBySpecificField(fields as IRepertoireField[], "order");
    }

    return ComponentsHelper.orderFieldsBySpecificField(
        fieldsToRender as IRepertoireField[],
        "order"
    );
};

const applyUsageDetailsRowFormatting = (
    fieldsToRender: any,
    componentInstance: string
): any => {
    if (componentInstance == USAGE_DETAILS_ROWS_SECTION_KEY && Array.isArray(fieldsToRender)) {
        return fieldsToRender.map((item: any) => ({
            ...item,
            ...(item.share !== undefined ? { share: roundDecimals(item.share, 2) } : {}),
        }));
    }

    return fieldsToRender;
};

const getFieldsToRender = ({
    tableContents,
    paginatedTableContents,
    componentInstance,
    currentPage,
    resultsPerPage,
    currentPageFromProps,
    resultsPerPageFromProps,
}: {
    tableContents: any;
    paginatedTableContents?: any;
    componentInstance: string;
    currentPage: number;
    resultsPerPage: number;
    currentPageFromProps?: number;
    resultsPerPageFromProps?: number;
}): any => {
    let fieldsToRender: any =
        paginatedTableContents && paginatedTableContents.length > 0
            ? paginatedTableContents
            : tableContents;

    if (PAGINATED_COMPONENTS.includes(componentInstance)) {
        fieldsToRender = getPaginatedFieldsWithOriginalIndex(
            tableContents,
            currentPage,
            resultsPerPage
        );
    }

    if (PAGINATED_MAGAZINE_COMPONENTS.includes(componentInstance)) {
        fieldsToRender = getPaginatedFieldsWithOriginalIndex(
            tableContents,
            currentPage,
            resultsPerPage
        );
    }

    if (componentInstance === DISTRIBUTION_POOLS_KEY) {
        fieldsToRender = getDistributionPoolFieldsWithOriginalIndex(
            tableContents,
            currentPageFromProps,
            resultsPerPageFromProps
        );
    }

    fieldsToRender = orderFieldsToRenderIfNeeded(fieldsToRender, componentInstance);
    fieldsToRender = applyUsageDetailsRowFormatting(fieldsToRender, componentInstance);

    return fieldsToRender;
};

const applyUsageRowInputTypes = (
    tableContentItem: any,
    pool?: IUsagePoolState
): any => {
    if (pool && pool.datesAndDimensions.length > 0) {
        pool.datesAndDimensions.forEach(element => {
            const startDate = new Date(element.effectiveFrom);
            const endDate = element.effectiveTo != null ? new Date(element.effectiveTo) : new Date();
            const dateToCheck = new Date(tableContentItem.usageDate.value);

            if (dateToCheck >= startDate && dateToCheck <= endDate) {
                tableContentItem.use.inputType = element.use ? "text" : "row";
                tableContentItem.dimension1.inputType = element.dimension1 ? "text" : "row";
                tableContentItem.dimension2.inputType = element.dimension2 ? "text" : "row";
                tableContentItem.dimension3.inputType = element.dimension3 ? "text" : "row";
                tableContentItem.dimension4.inputType = element.dimension4 ? "text" : "row";
                tableContentItem.dimension5.inputType = element.dimension5 ? "text" : "row";
            } else {
                tableContentItem.use.inputType = "row";
                tableContentItem.dimension1.inputType = "row";
                tableContentItem.dimension2.inputType = "row";
                tableContentItem.dimension3.inputType = "row";
                tableContentItem.dimension4.inputType = "row";
                tableContentItem.dimension5.inputType = "row";
            }
        });
    } else {
        tableContentItem.use.inputType = "row";
        tableContentItem.dimension1.inputType = "row";
        tableContentItem.dimension2.inputType = "row";
        tableContentItem.dimension3.inputType = "row";
        tableContentItem.dimension4.inputType = "row";
        tableContentItem.dimension5.inputType = "row";
    }

    return tableContentItem;
};

const renderEditableFieldsGridRow = ({
    index,
    tableContentItem,
    tableContents,
    sections,
    handleDrop,
    handleDragLeave,
    handleDragEnd,
    handleDragOver,
    handleDragEnter,
    handleDragStart,
}: {
    index: any;
    tableContentItem: any;
    tableContents: any;
    sections: JSX.Element[];
    handleDrop: (newOrder: any, e: any, droppedOnItem: any, tableContents: any) => void;
    handleDragLeave: (index: any, e: any, tableContentItem: any, tableContents: any) => void;
    handleDragEnd: (index: any, e: any, tableContentItem: any, tableContents: any) => void;
    handleDragOver: (index: any, e: any, tableContentItem: any, tableContents: any) => void;
    handleDragEnter: (index: any, e: any, tableContentItem: any, tableContents: any) => void;
    handleDragStart: (index: any, e: any, draggedItem: any, tableContents: any) => void;
}) => {
    return (
        <div
            key={index}
            draggable={true}
            className="row daggableItem"
            onDrop={e => handleDrop(index, e, tableContentItem, tableContents)}
            onDragLeave={e => handleDragLeave(index, e, tableContentItem, tableContents)}
            onDragEnd={e => handleDragEnd(index, e, tableContentItem, tableContents)}
            onDragOver={e => handleDragOver(index, e, tableContentItem, tableContents)}
            onDragEnter={e => handleDragEnter(index, e, tableContentItem, tableContents)}
            onDragStart={e => handleDragStart(index, e, tableContentItem, tableContents)}
        >
            {sections}
        </div>
    );
};

const renderUsageRow = ({
    index,
    tableContentItem,
    dataGridTableData,
    lookupValues,
    addUsageDetailsRowData,
    onUpdateUsageRowDetails,
    changeData,
    showModal,
    addUsageDetailsShareIPRow,
    tableContents,
    resetMessageBanner,
    focusedRow,
    expandedUsageDetailsRow,
    onRowClick,
    pool,
}: {
    index: number;
    tableContentItem: any;
    dataGridTableData: IRepertoireComponentDataItem;
    lookupValues?: ILookupDictionary;
    addUsageDetailsRowData?: any;
    onUpdateUsageRowDetails?: (value: IUsageDetailsRowData[], fieldName: string, index: number) => void;
    changeData?: any;
    showModal?: any;
    addUsageDetailsShareIPRow?: any;
    tableContents: any;
    resetMessageBanner?: () => void;
    focusedRow: number;
    expandedUsageDetailsRow?: number[];
    onRowClick: (rowNumber: number) => void;
    pool?: IUsagePoolState;
}) => {
    const usage = applyUsageRowInputTypes(tableContentItem, pool);

    return (
        <UsageRowView
            onRowClick={() => onRowClick(index)}
            onExpandAllClicked={null}
            key={index}
            usage={usage}
            index={index}
            dataGridTableData={dataGridTableData}
            lookupValues={lookupValues}
            addUsageDetailsRowData={addUsageDetailsRowData}
            onUpdateUsageRowDetails={onUpdateUsageRowDetails}
            changeData={changeData}
            showModal={showModal}
            addUsageDetailsShareIPRow={addUsageDetailsShareIPRow}
            tableContents={tableContents}
            resetMessageBanner={resetMessageBanner}
            focusedRowNumber={focusedRow}
            expandedUsageDetailsRow={expandedUsageDetailsRow}
        />
    );
};

const renderUsageMatchingRow = ({
    index,
    tableContentItem,
    dataGridTableData,
    stateKey,
    componentInstance,
    changeData,
    tableContents,
    isExpandable,
    dependentRowAction,
    clearAllRowsButThis,
    openWorkFlow,
    focusedRow,
    isExpandedUsageMatchingSection,
    onRowClick,
}: any) => {
    return (
        <DataGridTableRow
            onRowClick={() => onRowClick(index)}
            dataGridTableData={dataGridTableData}
            stateKey={stateKey}
            componentInstance={componentInstance}
            tableContentsItem={tableContentItem}
            key={index}
            index={index}
            changeData={changeData}
            tableContents={tableContents}
            isExpandable={isExpandable}
            dependentRowAction={dependentRowAction}
            clearAllRowsButThis={clearAllRowsButThis}
            openWorkFlow={openWorkFlow}
            focusedRowNumber={focusedRow}
            isExpandedUsageMatchingSection={isExpandedUsageMatchingSection}
        />
    );
};

const renderIpLicensingRepresentationRow = ({
    index,
    tableContentItem,
    dataGridTableData,
    stateKey,
    componentInstance,
    changeData,
    tableContents,
    isExpandable,
    dependentRowAction,
    updateIpRepresentationsIdsToRemove,
    focusedRow,
    onRowClick,
}: any) => {
    return (
        <DataGridTableRow
            onRowClick={() => onRowClick(index)}
            dataGridTableData={dataGridTableData}
            stateKey={stateKey}
            componentInstance={componentInstance}
            tableContentsItem={tableContentItem}
            key={index}
            index={index}
            changeData={changeData}
            tableContents={tableContents}
            isExpandable={isExpandable}
            dependentRowAction={dependentRowAction}
            updateIpRepresentationsIdsToRemove={updateIpRepresentationsIdsToRemove}
            focusedRowNumber={focusedRow}
        />
    );
};

const renderUsageProductDetailsRow = ({
    index,
    tableContentItem,
    dataGridTableData,
    stateKey,
    componentInstance,
    tableContents,
    isExpandable,
    focusedRow,
    onRowClick,
}: any) => {
    return (
        <DataGridTableRow
            onRowClick={() => onRowClick(index)}
            dataGridTableData={dataGridTableData}
            stateKey={stateKey}
            componentInstance={componentInstance}
            tableContentsItem={tableContentItem}
            key={index}
            index={index}
            tableContents={tableContents}
            isExpandable={isExpandable}
            focusedRowNumber={focusedRow}
        />
    );
};

const renderDefaultRow = ({
    index,
    sections,
    focusedRow,
    onRowClick,
}: {
    index: number;
    sections: JSX.Element[];
    focusedRow: number;
    onRowClick: (rowNumber: number) => void;
}) => {
    return (
        <tr
            key={index}
            onClick={() => onRowClick(index)}
            className={`tr ${focusedRow === index && "focusedTableRow"}`}
        >
            {sections}
        </tr>
    );
};

export const renderTableContents = ({
    tableContents,
    paginatedTableContents,
    addUsageDetailsRowData,
    onUpdateUsageRowDetails,
    isExpandedUsageMatchingSection,
    componentInstance,
    customFields,
    pool,
    dataGridTableData,
    isExpandable,
    stateKey,
    changeData,
    expandedUsageDetailsRow,
    addUsageDetailsShareIPRow,
    lookupValues,
    showModal,
    portalType,
    resetMessageBanner,
    openWorkFlow,
    focusedRow,
    currentPage,
    resultsPerPage,
    currentPageFromProps,
    resultsPerPageFromProps,
    enableCustomField,
    clearAllRowsButThis,
    getTableSections,
    renderRowAction,
    renderFieldOptions,
    changeTableData,
    showSearchModal,
    onClickOpen,
    onRowClick,
    dependentRowAction,
    updateIpRepresentationsIdsToRemove,
    handleDrop,
    handleDragLeave,
    handleDragEnd,
    handleDragEnter,
    handleDragOver,
    handleDragStart,
    propsForTableSections,
}: IRenderTableContentsParams): JSX.Element[] => {
    const rows: JSX.Element[] = [];

    if (!tableContents) {
        return rows;
    }

    let fieldsToRender = getFieldsToRender({
        tableContents,
        paginatedTableContents,
        componentInstance,
        currentPage,
        resultsPerPage,
        currentPageFromProps,
        resultsPerPageFromProps,
    });

    fieldsToRender &&
        fieldsToRender?.forEach((tableContentItem: any, index: any) => {
            if (COMPONENTS_WITH_ORIGINAL_INDEX.includes(componentInstance)) {
                index = tableContentItem.originalIndex;
            }

            const items = getTableSections(
                tableContentItem,
                index,
                propsForTableSections,
                renderRowAction,
                renderFieldOptions,
                changeTableData,
                showSearchModal,
                onClickOpen
            );

            const sections: JSX.Element[] = items.sections;
            const dependentSections: JSX.Element[] = items.dependentSections;

            if (componentInstance != "contributors" && isTableAction(tableContentItem.value)) {
                sections.push(renderRowAction(tableContentItem, index));
            } else if (
                componentInstance === "instrumentation" &&
                tableContentItem.type.value === "CUSTOM" &&
                portalType !== MEMBERS_PORTAL
            ) {
                if (!customFields || !customFields.includes(index)) {
                    enableCustomField(index);
                }
            } else if (
                componentInstance === "instrumentation" &&
                tableContentItem.type.value !== "CUSTOM" &&
                portalType !== MEMBERS_PORTAL
            ) {
                if (customFields && customFields.includes(index)) {
                    enableCustomField(index);
                }
            } else {
                const removeSectionIndex = sections.findIndex(e => e.key === "remove");

                if (removeSectionIndex >= 0) {
                    sections[removeSectionIndex] = renderRowAction(tableContentItem, index);
                }
            }

            if (componentInstance === EDITABLE_FIELDS_GRID) {
                rows.push(
                    renderEditableFieldsGridRow({
                        index,
                        tableContentItem,
                        tableContents,
                        sections,
                        handleDrop,
                        handleDragLeave,
                        handleDragEnd,
                        handleDragOver,
                        handleDragEnter,
                        handleDragStart,
                    })
                );
            } else if (componentInstance === USAGE_ROWS_SECTION_KEY) {
                rows.push(
                    renderUsageRow({
                        index,
                        tableContentItem,
                        dataGridTableData,
                        lookupValues,
                        addUsageDetailsRowData,
                        onUpdateUsageRowDetails,
                        changeData,
                        showModal,
                        addUsageDetailsShareIPRow,
                        tableContents,
                        resetMessageBanner,
                        focusedRow,
                        expandedUsageDetailsRow,
                        onRowClick,
                        pool,
                    })
                );
            } else if (
                componentInstance === USAGE_MATCHING_WORKS_SECTION_KEY ||
                componentInstance === USAGE_MATCHING_PRODUCTS_SECTION_KEY ||
                componentInstance === USAGE_LICENSE_MATCHING
            ) {
                rows.push(
                    renderUsageMatchingRow({
                        index,
                        tableContentItem,
                        dataGridTableData,
                        stateKey,
                        componentInstance,
                        changeData,
                        tableContents,
                        isExpandable,
                        dependentRowAction,
                        clearAllRowsButThis,
                        openWorkFlow,
                        focusedRow,
                        isExpandedUsageMatchingSection,
                        onRowClick,
                    })
                );
            } else if (componentInstance === IP_LICENSING_REPRESENTATION_KEY) {
                rows.push(
                    renderIpLicensingRepresentationRow({
                        index,
                        tableContentItem,
                        dataGridTableData,
                        stateKey,
                        componentInstance,
                        changeData,
                        tableContents,
                        isExpandable,
                        dependentRowAction,
                        updateIpRepresentationsIdsToRemove,
                        focusedRow,
                        onRowClick,
                    })
                );
            } else if (componentInstance === USAGE_PRODUCT_DETAILS_ROWS_SECTION_KEY) {
                rows.push(
                    renderUsageProductDetailsRow({
                        index,
                        tableContentItem,
                        dataGridTableData,
                        stateKey,
                        componentInstance,
                        tableContents,
                        isExpandable,
                        focusedRow,
                        onRowClick,
                    })
                );
            } else {
                rows.push(
                    renderDefaultRow({
                        index,
                        sections,
                        focusedRow,
                        onRowClick,
                    })
                );
            }

            rows.push(...dependentSections);
        });

    return rows;
};